/**
 * FeatureFlagGuard
 *
 * Client-side route guard that redirects to "/" when a mobile feature flag is
 * disabled for the current tenant. Mirrors the mobile deep-link guard pattern
 * (useMobileFeatures + router.replace("/(tabs)") in each screen).
 *
 * Fail-closed: when flags have not yet loaded OR when the flags fetch errored
 * with no cached data, the guard renders nothing (keeps the route invisible)
 * rather than falling back to all-true defaults that would let a disabled
 * feature through during a transient API failure.
 *
 * Usage in App.tsx:
 *   <Route path="/meditation">
 *     {() => <FeatureFlagGuard flagKey="meditation"><MeditationPage /></FeatureFlagGuard>}
 *   </Route>
 */
import React from "react";
import { Redirect } from "wouter";
import { useFeatureFlags, type FeatureFlagKey } from "@/hooks/useFeatureFlags";

interface FeatureFlagGuardProps {
  /** The feature-flag key to check (matches MOBILE_FEATURE_KEYS on the server). */
  flagKey: FeatureFlagKey;
  /** The page content to render when the flag is on. */
  children: React.ReactNode;
}

export function FeatureFlagGuard({ flagKey, children }: FeatureFlagGuardProps) {
  const { flags, isLoading } = useFeatureFlags();

  // Still loading: render nothing to avoid a visible flash of content or a
  // premature redirect before the real flag value arrives.
  if (isLoading) return null;

  // flags is null when:
  //   (a) the fetch errored and there is no cached data — fail closed.
  //   (b) the server returned 401 (handled by getQueryFn on401:"returnNull").
  // In both cases we redirect to home rather than silently allowing access.
  if (!flags || !flags[flagKey]) return <Redirect to="/" />;

  return <>{children}</>;
}
